// src/components/events/PendingEvents.jsx
// Pending Events view (Phase 4 §6) — the steward's own unpublished imported
// events, grouped by import date. Publish selected / Edit only — no Discard
// control (struck 5 August 2026, see docs/briefs/2026-08-05-events-importer-phase4.md §0c).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsList from '../core/HeadersAndFooters/FooterButtonsList';
import StandardButton from '../core/StandardButton';
import AddEditEvent from './AddEditEvent';
import FullScreenOverlay from '../core/FullScreenOverlay';
import { useTranslation } from '../../i18n/useTranslation';
import { getPendingEvents, getEventArticleById, publishEvents } from '../../data/eventsImportService';
import { useTagHouseFallback } from '../../hooks/useTagHouseFallback';

function groupByImportDate(events) {
  const groups = new Map();
  events.forEach(ev => {
    const key = ev.importedAt ? new Date(ev.importedAt).toISOString().slice(0, 10) : 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ev);
  });
  return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

const PendingEvents = ({ currentUser, onBack, currentContext }) => {
  const { t, formattingLocale } = useTranslation('importer');
  // R4 house fallback (image-layer build) — null until fetched.
  const houseFallback = useTagHouseFallback();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [listError, setListError] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);

  const loadEvents = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const data = await getPendingEvents(currentUser.id);
    setEvents(data);
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const groups = useMemo(() => groupByImportDate(events), [events]);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => prev.size === events.length ? new Set() : new Set(events.map(e => e.id)));
  };

  const toggleGroup = (groupEvents) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = groupEvents.every(e => next.has(e.id));
      groupEvents.forEach(e => allSelected ? next.delete(e.id) : next.add(e.id));
      return next;
    });
  };

  const handlePublish = async () => {
    setPublishing(true);
    setListError(null);
    const ids = [...selectedIds];
    const { failed } = await publishEvents(ids);
    if (failed.length > 0) {
      console.error('Some events failed to publish:', failed);
      setListError(t('publish_partial_failure', { count: failed.length }));
    }
    setSelectedIds(new Set());
    setPublishing(false);
    loadEvents();
    window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));
  };

  const handleEditClick = async (eventId) => {
    try {
      const article = await getEventArticleById(eventId);
      setEditingArticle(article);
    } catch (err) {
      console.error('Failed to load event for editing:', err);
      setListError(t('error_event_load_failed'));
    }
  };

  const handleEditClose = () => {
    setEditingArticle(null);
    loadEvents();
  };

  return (
    <>
      <HeaderContainer />
      <HeaderButtonsForm currentContext={currentContext} onBack={onBack} headerTitle={t('pending_title')} />
      <div style={{ marginTop: 120, marginBottom: 140, padding: '0 24px', maxWidth: 900, marginLeft: 'auto', marginRight: 'auto' }}>

        {loading && <p>{t('loading')}</p>}

        {!loading && events.length === 0 && (
          <p>{t('pending_empty_state')}</p>
        )}

        {!loading && events.length > 0 && (
          <>
            {listError && <p style={{ color: '#B45309', marginBottom: 12 }}>{listError}</p>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <label>
                <input type="checkbox" checked={selectedIds.size === events.length} onChange={toggleAll} />
                {' '}{t('select_all')}
              </label>
              <StandardButton
                variant="action"
                label={t('button_publish_selected', { count: selectedIds.size })}
                disabled={selectedIds.size === 0 || publishing}
                confirmBefore={true}
                confirmTitle={t('publish_confirm_title')}
                confirmMessage={t('publish_confirm_message', { count: selectedIds.size })}
                confirmOkLabel={t('publish_confirm_ok')}
                onClick={handlePublish}
              />
            </div>

            {groups.map(([dateKey, groupEvents]) => (
              <section key={dateKey} style={{ marginBottom: 24 }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {dateKey === 'unknown' ? t('unknown_import_date') : t('imported_on_date', { date: new Date(dateKey).toLocaleDateString(formattingLocale) })}
                  <label style={{ fontSize: 13, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={groupEvents.every(e => selectedIds.has(e.id))}
                      onChange={() => toggleGroup(groupEvents)}
                    />
                    {' '}{t('select_all_in_group')}
                  </label>
                </h3>

                {groupEvents.map(ev => (
                  <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <input type="checkbox" checked={selectedIds.has(ev.id)} onChange={() => toggleOne(ev.id)} />
                    {(ev.thumbnailUrl || houseFallback) && (
                      // §5a — never crop the illustration's own border;
                      // contain + white (this row has no panel background of
                      // its own, so white matches the page) as the letterbox
                      // fill.
                      <img
                        src={ev.thumbnailUrl || houseFallback}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'contain', backgroundColor: 'white', borderRadius: 4 }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{ev.title}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>
                        {ev.startDate ? new Date(ev.startDate).toLocaleDateString(formattingLocale) : ''}
                        {ev.freguesiaName ? ` · ${ev.freguesiaName}` : ''}
                      </div>
                    </div>
                    <StandardButton variant="ghost-action" label={t('button_edit')} onClick={() => handleEditClick(ev.id)} />
                  </div>
                ))}
              </section>
            ))}
          </>
        )}
      </div>
      <FooterContainer>
        <FooterButtonsList showGoToTop={false} />
      </FooterContainer>

      {editingArticle && (
        // The shared full-screen edit window (#78, addendum 1 item 1).
        <FullScreenOverlay onClose={handleEditClose}>
            <AddEditEvent
              userId={currentUser.id}
              onBack={handleEditClose}
              currentContext={currentContext}
              mode="edit"
              event={editingArticle}
            />
        </FullScreenOverlay>
      )}
    </>
  );
};

export default PendingEvents;
